import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = { class: "reset-multiselect-container" }
const _hoisted_3 = { class: "multiselect multiselect-wrapper" }
const _hoisted_4 = { class: "multiselect__tag" }
const _hoisted_5 = { key: 0 }

import { VIconSvg, VButton } from "@/components";
import VErrorTooltip from "../VErrorTooltip/VErrorTooltip.vue";
import { has, includes, isEqual } from "lodash";
import { computed, watch, ref, nextTick } from "vue";
import store from "@/store";
import { getRandomId } from "@/mixins/global.mixin";
import i18n from "@/plugins/vue-i18n";
import { TranslateResult } from "vue-i18n";

interface Props {
  value?: Array<string | number>;
  options?: Array<{
    label: string;
    code: string | number;
  }>;
  label?: string | TranslateResult;
  labelPrepend?: boolean;
  itemLabel?: string;
  trackBy?: string;
  placeholder?: string | TranslateResult;
  max?: number;
  disabled?: boolean;
  allowEmpty?: boolean;
  error?: string | TranslateResult;
  required?: boolean;
  description?: string | TranslateResult;
  customReset?: boolean;
  taggable?: boolean;
  editableTag?: boolean;
  fullValues?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VInputTag',
  props: {
    value: { default: () => [] },
    options: { default: () => [
    {
      label: "",
      code: "",
    },
  ] },
    label: { default: "" },
    labelPrepend: { type: Boolean, default: false },
    itemLabel: { default: "label" },
    trackBy: { default: "code" },
    placeholder: { default: "" },
    max: { default: 100 },
    disabled: { type: Boolean, default: false },
    allowEmpty: { type: Boolean, default: true },
    error: { default: "" },
    required: { type: Boolean, default: false },
    description: { default: "" },
    customReset: { type: Boolean, default: false },
    taggable: { type: Boolean, default: false },
    editableTag: { type: Boolean, default: false },
    fullValues: { type: Boolean, default: false }
  },
  emits: [
  "searchChange",
  "onClickResetButton",
  "input",
  "remove",
  "addTag",
  "edit",
],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

const touchStartedAt = ref<number | null>(null);
const isStartedTouchEvent = ref(false);
const multiselect = ref();

const isMobileDevice = computed(() => store.getters["viewport/isMobileDevice"]);
const id = computed(() => getRandomId());

const selectValue = computed({
  get() {
    return props.value.map((item) =>
      props.options.find((option) => {
        const value = props.fullValues ? item[props.trackBy] : item;

        return option[props.trackBy] === value;
      })
    );
  },

  set(data: Array<{ label: string; code: string | number }>) {
    const value = data.map((item) => {
      return props.fullValues ? item : item[props.trackBy];
    });

    if (!isEqual(value, props.value)) emit("input", value);
  },
});

const isShownInfoIcon = computed(() => !!props.description);
const isShownResetButton = computed(() => props.value.length);

const inputWrapperClasses = computed(() => {
  const classes = {
    "input-label-preppend": props.labelPrepend,
    "input-required": props.required,
    "shown-reset-button": isShownResetButton.value,
    "input-error": props.error,
  };

  return [classes];
});

const placeholderText = computed(() =>
  props.placeholder ? props.placeholder : i18n.t("placeholder.selectMultiple")
);
const selectLabel = computed(() => (isMobileDevice.value ? "" : i18n.t("label.selectLabel")));
const deselectLabel = computed(() => (isMobileDevice.value ? "" : i18n.t("label.deselectLabel")));
const selectedLabel = computed(() => (isMobileDevice.value ? "" : i18n.t("label.selectedLabel")));

const initSelectTapEvent = () => {
  const [optionsWrapper] = multiselect.value.$refs.list.children;

  Array.from(optionsWrapper.children).map((element, index) => {
    if (element.className === "multiselect__element") {
      const [option] = element.children;

      option.addEventListener("touchstart", () => {
        touchStartedAt.value = Date.now();
        multiselect.value.pointerSet(index);
        initTagTapEvent();
      });

      option.addEventListener("touchend", () => {
        const value = props.options[index];
        const duration = Date.now() - touchStartedAt.value;

        if (!includes(selectValue.value, value) && duration <= 150) {
          selectValue.value = selectValue.value.concat([value]);
        } else {
          selectValue.value = selectValue.value.filter((item) => !isEqual(item, value));
        }
      });
    }
  });
};

const initTagTapEvent = () => {
  if (multiselect.value) {
    nextTick(() => {
      const [optionsWrapper] = multiselect.value.$refs.tags.children;

      Array.from(optionsWrapper.children).map((element) => {
        let tagValue = "";

        Array.from(element.children).map((childElement) => {
          if (childElement.tagName === "SPAN") {
            tagValue = childElement.innerHTML;
          }

          if (childElement.className === "multiselect__tag-icon") {
            childElement.removeEventListener("touchstart", () => removeTag(), true);
            childElement.addEventListener("touchstart", () => {
              removeTag(tagValue);
            });
          }
        });
      });
    });
  }
};

watch(() => selectValue, initTagTapEvent, { deep: true, immediate: true });

const removeTag = (tagValue: string | number) => {
  let newValue = [...selectValue.value];

  newValue = newValue.filter((element) => element.label !== tagValue);
  selectValue.value = newValue;
};

const onClickWrapper = () => {
  const { isOpen, activate } = multiselect.value;

  if (!isOpen) activate();

  if (!isStartedTouchEvent.value) {
    initSelectTapEvent();
    isStartedTouchEvent.value = true;
  }
};

const onClickOutsideWrapper = () => {
  const { isOpen, deactivate } = multiselect.value;

  if (isOpen) deactivate();
};

const addTag = (newTag: string) => {
  emit("addTag", newTag);
};

const onClickResetButton = () => {
  props.customReset ? emit("onClickResetButton") : emit("input", []);
};

const onClickEdit = (option) => emit("edit", option);

const onSearchChange = (value: string) => {
  emit("searchChange", value);
};

const removeItem = () => {
  emit("remove");
};

return (_ctx: any,_cache: any) => {
  const _component_VueMultiselect = _resolveComponent("VueMultiselect")!
  const _directive_touch = _resolveDirective("touch")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["input-tag-wrapper", inputWrapperClasses.value])
  }, [
    (_ctx.label.length)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: "label",
          for: id.value
        }, [
          _createElementVNode("span", null, _toDisplayString(_ctx.label), 1),
          (isShownInfoIcon.value)
            ? (_openBlock(), _createBlock(_unref(VIconSvg), {
                key: 0,
                tooltip: _ctx.description,
                name: "InfoCircle2",
                color: "fc-stroke",
                size: "xs"
              }, null, 8, ["tooltip"]))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_3, [
        _createVNode(_component_VueMultiselect, {
          id: id.value,
          ref_key: "multiselect",
          ref: multiselect,
          modelValue: selectValue.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectValue).value = $event)),
          options: _ctx.options,
          label: _ctx.itemLabel,
          "track-by": _ctx.trackBy,
          placeholder: placeholderText.value,
          disabled: _ctx.disabled,
          "allow-empty": _ctx.allowEmpty,
          searchable: true,
          max: _ctx.max,
          multiple: true,
          "select-label": selectLabel.value,
          "deselect-label": deselectLabel.value,
          "selected-label": selectedLabel.value,
          taggable: _ctx.taggable,
          onRemove: removeItem,
          onTag: addTag,
          onSearchChange: onSearchChange
        }, {
          tag: _withCtx(({ option, remove }) => [
            _createElementVNode("span", _hoisted_4, [
              (_unref(has)(option, props.itemLabel))
                ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(option[props.itemLabel]), 1))
                : _createCommentVNode("", true),
              (props.editableTag)
                ? (_openBlock(), _createBlock(_unref(VIconSvg), {
                    key: 1,
                    name: "Edit",
                    size: "xs",
                    "color-tag": "stroke",
                    class: "option-icon",
                    onClick: ($event: any) => (onClickEdit(option))
                  }, null, 8, ["onClick"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(VIconSvg), {
                name: "CloseBold",
                size: "xs",
                "color-tag": "stroke",
                class: "option-icon",
                onClick: ($event: any) => (remove(option))
              }, null, 8, ["onClick"])
            ])
          ]),
          _: 1
        }, 8, ["id", "modelValue", "options", "label", "track-by", "placeholder", "disabled", "allow-empty", "max", "select-label", "deselect-label", "selected-label", "taggable"])
      ])), [
        [_directive_touch, onClickWrapper],
        [_directive_click_outside, onClickOutsideWrapper]
      ]),
      (isShownResetButton.value)
        ? (_openBlock(), _createBlock(_unref(VButton), {
            key: 0,
            class: "reset-button",
            text: _ctx.$t('button.resetAll'),
            variant: "secondary",
            "data-cy": "input-tag-reset-all-button",
            onClick: onClickResetButton
          }, null, 8, ["text"]))
        : _createCommentVNode("", true)
    ]),
    (_ctx.error)
      ? (_openBlock(), _createBlock(VErrorTooltip, {
          key: 1,
          error: _ctx.error
        }, null, 8, ["error"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})